// - newspaper_subname
// - contest_slug
// - contest_title
// - contest_type
// - contest_mode
// - contest_class_list
// - contest_description
// - contest_terms
// - start_at
// - end_at
// - result_date
// - image_name
// - image_description
// - image_width
// - image_height
// - image_copyright
// - question-answers
//     - question
//     - answers

import { getDateFromString } from 'app/shared/utils/utils';
import { environment } from 'environments/environment';

enum ContestStatus {
  soon,
  inProgress,
  completed,
  results
}
export { ContestStatus };

class Question {
  constructor(public id: string, public title: string) {}
}

class Answer {
  constructor(
    public id: string,
    public title: string,
    public is_correct: boolean
  ) {}
}

export class Prize {
  constructor(
    public title: string,
    public winners: string[],
    public image_url: string,
    public classification: string
  ) {}
}

class QA {
  constructor(public question: Question, public answers: Answer[]) {}

  get correct_answers() {
    return this.answers.filter(answer => answer.is_correct);
  }
}

export class Contest {
  static fromObject(object) {
    // create qa
    const qa: QA[] = [];
    if (object['questions-answers']) {
      object['questions-answers'].forEach(qa_item => {
        const question = new Question(
          qa_item.question.id,
          qa_item.question.title
        );
        const answers = [];
        qa_item.answers.forEach(answer => {
          answers.push(new Answer(answer.id, answer.title, answer.is_true));
        });
        qa.push(new QA(question, answers));
      });
    }

    const prizes: Prize[] = [];
    if (object['gifts']) {
      object['gifts'].forEach(prize => {
        prizes.push(
          new Prize(
            prize.title,
            prize.winners,
            prize.image_url,
            prize.classification
          )
        );
      });
    }

    return new Contest(
      object.contest_id,
      object.newspaper_subname,
      object.contest_slug,
      object.contest_partner_name,
      object.contest_title,
      object.contest_type,
      object.contest_mode,
      object.contest_class_list,
      object.contest_link_button_name,
      object.contest_link_button,
      object.contest_short_description,
      object.contest_description,
      object.contest_terms,
      getDateFromString(object.start_at),
      getDateFromString(object.end_at),
      getDateFromString(object.result_date),
      object.rules_url,
      object.image_url,
      object.image_name,
      object.image_description,
      object.image_width,
      object.image_height,
      object.image_copyright,
      qa,
      prizes
    );
  }

  constructor(
    public id: string,
    public newspaper_subname: string,
    public slug: string,
    public partner: string,
    public title: string,
    public type: string,
    public mode: string,
    public class_list: boolean | any[], // TO DO Class class
    public contest_button_text: string,
    public contest_button_link: string,
    public short_description: string,
    public description: string,
    public terms: string,
    public start: Date,
    public end: Date,
    public result: Date,
    public rules_url: string,
    public image_url: string,
    public image_name: string,
    public image_description: string,
    public image_width: string,
    public image_height: string,
    public image_copyright: string,
    public questions: QA[],
    public prizes: Prize[]
  ) {}

  get hasWinners() {
    return this.prizes.some(prize => prize.winners.length > 0);
  }

  get status() {
    const date = new Date();
    if (date > this.start && date < this.end) {
      return ContestStatus.inProgress;
    } else if (date < this.start) {
      return ContestStatus.soon;
    } else if (date > this.end && date < this.result) {
      return ContestStatus.completed;
    } else {
      return ContestStatus.results;
    }
  }

  get dates() {
    if (this.status === ContestStatus.inProgress) {
      return (
        `Du ${this.start.getDate()} ${this.start.toLocaleString(
          environment.locale,
          { month: 'long' }
        )}` +
        ` au ${this.end.getDate()} ${this.end.toLocaleString(
          environment.locale,
          { month: 'long' }
        )} ${this.end.getFullYear()}`
      );
    } else if (this.status === ContestStatus.soon) {
      return (
        `À partir du ${this.start.getDate()}` +
        ` ${this.start.toLocaleString(environment.locale, { month: 'long' })}` +
        ` ${this.start.getFullYear()}`
      );
    } else {
      return `Résultats : à partir du ${this.result.getDate()} ${this.result.toLocaleString(
        environment.locale,
        { month: 'long' }
      )}`;
    }
  }

  get statusText() {
    if (this.status === ContestStatus.inProgress) {
      return 'En cours';
    } else if (this.status === ContestStatus.soon) {
      return 'Prochainement';
    } else {
      return 'Concours terminé';
    }
  }

  get buttonText() {
    if (this.status === ContestStatus.inProgress) {
      return 'Participer';
    } else if (this.status === ContestStatus.soon) {
      return 'Recevoir une alerte';
    } else {
      return 'Voir les résultats';
    }
  }
}
